import type { ChangeEvent } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { BackgroundColorProps, BackgroundImageProps, BorderColorProps } from 'styled-system';
import { backgroundColor, backgroundImage, borderColor, color, width } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { OutlineSelect } from 'common/components/Select';
import type { Language } from 'common/providers/LocaleProvider';
import { getUrlPrefix } from 'common/utils/url';
import arrowDown from './assets/arrow-down-white.svg';
import { trackLanguageChange, trackLanguageClick } from './tracking';

interface LanguageSwitchProps {
  languages: Language[];
}

const LanguageSelect = styled(OutlineSelect)<
  BackgroundColorProps & BackgroundImageProps & BorderColorProps
>`
  ${width};
  ${backgroundColor};
  ${backgroundImage};
  ${color};
  ${borderColor};
`;

const languageSelectStyles = {
  width: 1,
  backgroundColor: 'transparent',
  backgroundImage: `url('${arrowDown}')`,
  color: 'sys.neutral.border.inverted',
  borderColor: 'sys.neutral.border.inverted',
};

const LanguageSwitch = ({ languages = [] }: LanguageSwitchProps) => {
  const [currentLanguage, setCurrentLanguage] = useState(() =>
    languages.find(({ isCurrent }) => isCurrent)
  );

  if (!languages || languages?.length < 2) {
    return null;
  }

  const selectHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = languages.find(({ language }) => language === event.target.value);
    trackLanguageChange(newLanguage.language);
    setCurrentLanguage(newLanguage);
    window.location.href = getUrlPrefix(newLanguage.pathPrefix);
  };

  return (
    <Flex
      pb="sp_16"
      px={['sp_16', null, 'sp_40', 'sp_64']}
      backgroundColor="sys.neutral.background.strong"
    >
      <LanguageSelect
        options={languages.map(({ language }) => language)}
        value={currentLanguage?.language}
        onClick={trackLanguageClick}
        onSelect={selectHandler}
        {...languageSelectStyles}
      />
    </Flex>
  );
};

export default LanguageSwitch;
